import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppShell from 'components/shell/AppShell';
import { readLastVerificationEmailSentAt, recordVerificationEmailSent, useAuthStatus, useResendVerificationEmail, } from 'features/auth';
const RESEND_COOLDOWN_MS = 60000;
function secondsRemaining(lastSentAt) {
    if (!lastSentAt)
        return 0;
    const elapsed = Date.now() - lastSentAt;
    if (elapsed >= RESEND_COOLDOWN_MS)
        return 0;
    return Math.ceil((RESEND_COOLDOWN_MS - elapsed) / 1000);
}
export default function VerifyEmailScreen() {
    const { t } = useTranslation();
    const status = useAuthStatus();
    const resend = useResendVerificationEmail();
    const email = status.kind === 'email-unverified' ? status.user.email : '';
    const displayName = status.kind === 'email-unverified' ? status.user.profile.displayName : '';
    const [lastSentAt, setLastSentAt] = useState(() => readLastVerificationEmailSentAt(email));
    const [cooldown, setCooldown] = useState(() => secondsRemaining(lastSentAt));
    const [feedback, setFeedback] = useState('');
    useEffect(() => {
        setLastSentAt(readLastVerificationEmailSentAt(email));
    }, [email]);
    useEffect(() => {
        if (!lastSentAt) {
            setCooldown(0);
            return;
        }
        setCooldown(secondsRemaining(lastSentAt));
        const id = window.setInterval(() => {
            setCooldown((prev) => {
                const next = secondsRemaining(lastSentAt);
                if (next === 0 && prev !== 0)
                    window.clearInterval(id);
                return next;
            });
        }, 1000);
        return () => window.clearInterval(id);
    }, [lastSentAt]);
    if (status.kind !== 'email-unverified') {
        return null;
    }
    const shellUser = {
        name: displayName,
        email,
        potentialActions: {},
    };
    const handleResend = async () => {
        if (cooldown > 0 || resend.isPending)
            return;
        setFeedback('');
        try {
            await resend.mutateAsync();
            recordVerificationEmailSent(email);
            setLastSentAt(Date.now());
            setFeedback(t('auth_verification_email_sent'));
        }
        catch (error) {
            setFeedback(error?.message || 'Failed to resend verification email.');
        }
    };
    const resendDisabled = cooldown > 0 || resend.isPending;
    const resendLabel = cooldown > 0
        ? t('auth_verification_resend_cooldown', { seconds: cooldown })
        : resend.isPending
            ? t('auth_verification_resend_sending')
            : t('auth_verification_resend');
    return (_jsx(AppShell, { user: shellUser, hideTabs: true, children: _jsxs("div", { className: "flex w-full max-w-2xl flex-col gap-4", children: [_jsx("div", { className: "rounded border border-primary-020 bg-primary-005 px-5 py-4 text-[16px] leading-6 text-primary", children: t('auth_verification_banner', { email }) }), _jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx("button", { disabled: resendDisabled, onClick: () => void handleResend(), className: "inline-flex h-10 items-center justify-center rounded border border-primary bg-primary px-6 text-[14px] font-bold leading-5 text-white disabled:opacity-50", type: "button", children: resendLabel }), feedback && _jsx("span", { className: "text-[14px] leading-5 text-primary", children: feedback })] })] }) }));
}
